import { capitalize } from '@/helpers'
import { useSidePanelHelpers } from './useSidePanelHelpers'

const DEFAULT_QUESTION_ATTRIBUTES = {
  quiz: {
    answers_attributes: [
      { text: "", correct: false, explanation: "" }
    ]
  },
  poll: {
    answers_attributes: [
      { text: "" }
    ]
  },
  open: {
    example_answer: ""
  }
}

export function useQuestionHelpers() {
  const { replaceInStack } = useSidePanelHelpers()

  const onAfterCreate = (slides, response, options = {}) => {
    slides.value = slides.value.map(slide => {
      if (slide.id === response.slide.id) {
        return response.slide
      }
      return slide
    })

    const block = slides.value.find(s => s.id === response.slide.id).blocks.find(b => b.block_type === 'section')
    const question = options.getQuestion
    ? options.getQuestion(block)
    : block.section.questions[block.section.questions.length - 1]

    replaceInStack({
      content: {
        component: 'QuestionForm',
        title: 'Edit question',
        props: {
          block,
          question,
          ...options.props
        },
        ...options.content
      }
    })
  }

  const hydrateNewQuestion = (type, block, options = {}) => {
    return {
      questionnaire_id: block.section.id,
      questionnaire_type: "Section",
      question_type: type,
      position: block.section.questions.length + 1,
      position_within_lesson: (block.section.questions[block.section.questions.length - 1]?.position_within_lesson || 0) + 1,
      variant_name: type,
      variant_type: `Question::${capitalize(type)}`,
      optional: false,
      text: "",
      variant_attributes: structuredClone(DEFAULT_QUESTION_ATTRIBUTES[type]),
      ...options
    }
  }

  // After a save the server owns the ids. The response carries them, but the save
  // replaces the slide's object graph wholesale, so the open form's detached copy never
  // hears about them — and its next autosave would post the same answers id-less,
  // creating them again. Landing the ids on the form's own objects is what stops that.
  const syncSavedIds = (questions, savedQuestions) => {
    const knownIds = new Set(questions.map(question => question.id).filter(Boolean))

    questions.forEach(question => {
      // An edit can fork the question server-side (new question, variant and answer
      // ids); position pairs the form's question with its fork when the id misses.
      const saved = savedQuestions.find(candidate => candidate.id === question.id) ||
        savedQuestions.find(candidate => candidate.position === question.position && !knownIds.has(candidate.id))
      if (!saved) return

      question.id = saved.id
      syncVariantIds(question, saved)
    })
  }

  const syncVariantIds = (question, saved) => {
    const variant = question.variant_attributes
    const savedVariant = saved.variant_attributes
    if (!variant || !savedVariant) return

    variant.id = savedVariant.id
    if (!variant.answers_attributes) return

    const savedAnswers = savedVariant.answers_attributes || []
    const savedIds = new Set(savedAnswers.map(answer => answer.id))

    // A _destroy the server accepted is gone; keeping the entry would resend the id.
    // One a stale response still carries hasn't landed — it stays until a save posts it.
    variant.answers_attributes = variant.answers_attributes.filter(answer =>
      !answer._destroy || savedIds.has(answer.id))

    // The server serializes answers in creation order, which is the order the form
    // posted them in — so its id-less (or fork-orphaned) answers claim the ids the
    // form doesn't know yet, in order. A stale response has nothing to claim — never
    // null an id a newer save already landed, or the next autosave recreates the row.
    const unclaimed = savedAnswers.filter(answer =>
      !variant.answers_attributes.some(candidate => candidate.id === answer.id))

    variant.answers_attributes.forEach(answer => {
      if (savedIds.has(answer.id)) return

      const claimed = unclaimed.shift()
      if (claimed) answer.id = claimed.id
    })
  }

  return {
    onAfterCreate,
    hydrateNewQuestion,
    syncSavedIds
  }
}
